import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import i18n from '@nextroll/ar-i18n';
import { RWListRules } from '@nextroll/ar-rollworks-list-builder';
import SlidingModal from '@nextroll/ar-sliding-modal';
import { ScrollingTable } from '@nextroll/ar-scrolling-table';
import { Alerts, InlineAlert, Tooltip, Popper, Placements } from '@nextroll/ar-style-base';

import { useRoflStore } from 'store/roflStore';
import { NotificationContext } from 'hooks/useNotification';
import { IWorkflowSchemaTriggerExclusions, CrmOptionType } from 'types/rofl';
import { IWorkflowAccountActionContactFilter } from 'types/roflWorkflowActions';
import { IContactByFilterContact, IContactByFilterContacts, getContactsByFilter } from 'api/workflowsApi';
import { getContactsByFilterCount, CONTACT_REDEMPTION_ACTIVITY_QUERY } from 'api/contactredemptionApi';

import contactEngageIcon from '../../../../assets/icons/contact-engage.png';


import { AccountActionPreviewContactInfoTooltip } from './AccountActionPreviewContactInfoTooltip';


const FILTERED_CONTACTS_ERROR_MSG = {
    type: Alerts.ALERT_TYPE_ERROR,
    title: i18n.gettext('Error occurred when finding contacts'),
    message: i18n.gettext('Please try again later.'),
};
const FILTERED_CONTACTS_ABORT_MSG = 'new request; abort';

const CONTACT_ENGAGE_ICON_WITH_TOOLTIP = (
    <Popper
        hoverContent={(
            <Tooltip placement={Placements.TOP}>
                {i18n.gettext('Preview unavailable for Hot Contacts not in CRM due to legal restrictions')}
            </Tooltip>
        )}
        showDelay={300}
        hideDelay={450}
        breakOutOfOverflow
    >
        <img src={contactEngageIcon} alt='icon' height={16} width={16} />
    </Popper>
);

/**
 * content elements displayed on the top of the contact preview table
 */
const CONTACT_ENGAGE_PREVIEW_TOP_ALERT = (
    <InlineAlert
        type={Alerts.ALERT_TYPE_INFO}
        title={i18n.gettext('Hot contacts filter applied')}
        message={i18n.gettext('These contacts have at least 1 page view or 1 ad click in the past 30 days. Due to legal restrictions, a preview is not available before purchase.')}
        showIcon
    />
);
const CONTACT_ENGAGE_PREVIEW_TOP_NOTE = (
    <p>
        {i18n.gettext(
            '<STRONG>Note:</STRONG> This preview shows all contacts that match your criteria. When it\'s time to purchase, we\'ll prioritize contacts based '
            + 'on Confidence Score, Seniority, and your Max Contacts per Account setting to make sure your credits are used on the best matches for you. '
            + '<LINK>Learn More</LINK>',
            {
                STRONG: <strong />,
                LINK: (
                    <a
                        href='https://help.rollworks.com/hc/articles/26202476299405-Contact-Discovery-Workflows-Acquire-Contacts-not-in-CRM#h_01HWRC9FNZESZ814RKKG9YH77M'
                        target='_blank'
                        rel='noopener noreferrer'
                    />
                )
            })}
    </p>
);


/**
 * below is the actual contact preview component
 */
interface IProps {
    advertisableEid?: string;
    crm?: CrmOptionType;
    title?: string;
}
export const AccountActionCrmContactFilterPreview = (props: IProps & {
    query: any;
    total?: number;
    open: boolean;
    setOpen: (open: boolean) => void;
    isContactEngagementOn: boolean;
}) => {
    const { advertisableEid, crm, query, total = 0, open, setOpen, isContactEngagementOn } = props;

    const { addAlert } = useContext(NotificationContext);
    const controller = useRef(new AbortController());

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ contacts: [] } as IContactByFilterContacts);

    const totalLabel = total !== undefined ? i18n.numeral(total).format('0,0') : '?';
    const remaining = total ? total - (data.contacts?.length || 0) : 100;

    /**
     * get formatted filtered contacts
     */
    const fetch = useCallback(async (next?: string) => {
        if (!advertisableEid || !crm || crm === 'other' || !query || loading) {
            return; // data is not ready; do nothing
        }
        setLoading(true);

        try {
            if (controller.current?.signal.aborted) {
                controller.current = new AbortController(); // previous request cancelled by panel close
            }
            const response = await getContactsByFilter(advertisableEid, crm, query, next, controller.current?.signal);
            response && setData(previousData => ({
                contacts: [...(previousData?.contacts || []), ...(response?.contacts || []).map(contact => ({
                    ...contact,
                    key: isContactEngagementOn // key is for table rendering
                        ? [contact.company_domain, uuidv4()].join('-')
                        : [contact.company_domain, contact.first_name, contact.last_name].join('-'),
                }))],
                next: response.next,
            }));
            setLoading(false);
        } catch (e) {
            setLoading(false);
            if (e === FILTERED_CONTACTS_ABORT_MSG || controller.current?.signal?.aborted) {
                return;
            }
            addAlert?.(FILTERED_CONTACTS_ERROR_MSG);
        }
    }, [advertisableEid, crm, query, isContactEngagementOn, loading, setLoading, addAlert]);

    /**
     * fetch the first batch of contacts
     */
    useEffect(() => {
        if (!open || !advertisableEid || !crm) {
            return;
        }
        fetch();
    }, [open, advertisableEid, crm]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * below are event handlers
     */
    const handleClose = useCallback(() => {
        // if there is ongoing request, terminate it before closing
        controller.current?.abort(FILTERED_CONTACTS_ABORT_MSG);

        setData({ contacts: [], next: undefined });
        setLoading(false);
        setOpen(false);
    }, [setData, setLoading, setOpen]);

    /**
     * footer contains the total number of contacts + the next page button
     */
    const footer = useMemo(() => (
        <div className='workflow-node-panel-account-action-filtered-contacts-panel-footer'>
            <p>
                <strong>
                    {!loading && !data.next
                        ? i18n.gettext('Showing all {{ total }} contacts', { total: totalLabel })
                        : i18n.gettext('Showing {{ size }} of {{ total }} contacts', { size: data.contacts?.length, total: totalLabel })}
                </strong>
            </p>
            <button type='button' role='button' className='btn btn-primary' disabled={loading || !data.next} onClick={() => data.next && fetch(data.next)}>
                {loading && (
                    <>
                        {i18n.gettext('Loading')}
                        &nbsp;
                        <div className='ar-spinner' />
                    </>
                )}
                {!loading && data.next && i18n.gettext('Load {{ size }} more', { size: remaining > 100 ? 100 : remaining })}
                {!loading && !data.next && i18n.gettext('Load more')}
            </button>
        </div>
    ), [loading, data, total]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * following 3 methods are for rendering specific cell components based on the given row info
     */
    const renderContactInfo = useCallback((row: IContactByFilterContact) => (
        <AccountActionPreviewContactInfoTooltip row={row} shouldMaskIcons={isContactEngagementOn} />
    ), [isContactEngagementOn]);
    const renderLinkedInLink = useCallback((row: IContactByFilterContact) => {
        if (!row.linkedin_url) {
            return ''; // empty string as placeholder to make the cell visible
        }
        const linkedInIcon = <i className='fab fa-linkedin' />;
        if (isContactEngagementOn) {
            return <span className='workflow-node-masked-cell'>{linkedInIcon}</span>;
        }
        return <a href={`https://${row.linkedin_url}`} target='_blank' rel='noreferrer'>{linkedInIcon}</a>;
    }, [isContactEngagementOn]);
    const renderContactName = useCallback((row: IContactByFilterContact) => {
        const name = [row.first_name, row.last_name].join(' ');
        if (isContactEngagementOn) {
            return (
                <>
                    {CONTACT_ENGAGE_ICON_WITH_TOOLTIP}
                    &nbsp;&nbsp;&nbsp;
                    <span className='workflow-node-masked-cell'>{name}</span>
                </>
            );
        }
        return name;
    }, [isContactEngagementOn]);

    return (
        <SlidingModal
            show={props.open}
            title={props.title || i18n.gettext('Previewing results: {{ total }} contacts match criteria', { total: totalLabel })}
            size={SlidingModal.SLIDING_MODAL_LARGE}
            className='workflow-node-panel-account-action-filtered-contacts-panel'
            footerContent={footer}
            onClose={handleClose}
        >
            {/* extra info before rendering the table content */}
            {isContactEngagementOn && CONTACT_ENGAGE_PREVIEW_TOP_ALERT}
            {CONTACT_ENGAGE_PREVIEW_TOP_NOTE}

            <ScrollingTable
                i18n={i18n}
                columns={[
                    {
                        key: 'name',
                        label: i18n.gettext('Contact Name'),
                        render: renderContactName,
                        textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
                    },
                    {
                        key: 'title',
                        label: i18n.gettext('Job Title'),
                        render: (row: IContactByFilterContact) => row.job_title,
                        textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
                    },
                    {
                        key: 'domain',
                        label: i18n.gettext('Account Domain'),
                        render: (row: IContactByFilterContact) => row.company_domain || '',
                        textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
                    },
                    {
                        key: 'contact_info',
                        label: i18n.gettext('Contact Info'),
                        render: renderContactInfo,
                        textAlignment: ScrollingTable.TEXT_ALIGN_CENTER,
                    },
                    {
                        key: 'linkedin',
                        label: i18n.gettext('LinkedIn'),
                        render: renderLinkedInLink,
                        textAlignment: ScrollingTable.TEXT_ALIGN_CENTER,
                    }

                ]}
                rowKey='key'
                isLoading={loading}
                data={data.contacts}
            />
        </SlidingModal>
    );
};

export const AccountActionCrmContactFilterPreviewButton = (props: IProps & {
    show?: boolean;
    accountRules?: { list_rule?: RWListRules } & IWorkflowSchemaTriggerExclusions;
    activityQuery?: boolean;
    contactRules?: IWorkflowAccountActionContactFilter[];
}) => {
    const { advertisableEid, crm, show, accountRules, activityQuery, contactRules } = props;

    const controller = useRef(new AbortController());

    // need to store credit info based on the config, which will be displayed in the settings page
    const updateExtraData = useRoflStore(state => state.updateExtraData);

    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [total, setTotal] = useState(undefined as { count: number; cost: number } | undefined);

    const query = useMemo(() => {
        const {
            list_rule, exclusions_types,
            not_in_crm_accounts, not_in_sales_marketing, not_in_advertising_filter, not_in_intent,
        } = accountRules || {};
        const contactFilterQuery = {
            account_list: {
                account_query: {
                    list_rule,
                    exclusions_types,
                    not_in_crm_accounts,
                    not_in_sales_marketing,
                    not_in_advertising_filter,
                    not_in_intent,

                    // if `not_in_sales_marketing` is true, `not_in_sales_marketing_since` must be provided
                    ...(not_in_sales_marketing ? { not_in_sales_marketing_since: 'P30D' } : {}),

                    // if `not_in_intent` is true, `not_in_intent_since` must be provided
                    ...(not_in_intent ? { not_in_intent_since: 'P30D' } : {}),
                },
            },
            ...(activityQuery ? CONTACT_REDEMPTION_ACTIVITY_QUERY : {}),
            ...(contactRules && contactRules.length > 0 ? {
                contact_query: {
                    list_rule: {
                        rules: contactRules || [],
                        rule_type: 'AND', // combine filtered rules together
                    },
                },
            } : {}), // the backend allows empty contact rules by simply omitting `contact_query`
        };
        updateExtraData('contactFilterQuery', contactFilterQuery);
        return contactFilterQuery;
    }, [accountRules, activityQuery, contactRules, updateExtraData]);

    /**
     * get total count of the filtered contacts
     */
    const fetch = useCallback(async () => {
        if (!advertisableEid || !crm || crm === 'other') {
            return; // data is not ready; do nothing
        }
        setLoading(true);

        try {
            // cancel any unfinished request to avoid throttling
            controller.current?.abort(FILTERED_CONTACTS_ABORT_MSG);
            controller.current = new AbortController();
            const countAndCost = await getContactsByFilterCount(
                advertisableEid,
                crm,
                undefined,
                query,
                controller.current?.signal
            );
            if (controller.current?.signal.aborted) {
                return;
            }
            countAndCost && setTotal(countAndCost);
            setLoading(false);
        } catch (e) {
            if (e === FILTERED_CONTACTS_ABORT_MSG || controller.current?.signal?.aborted) {
                return;
            }
            //
            // TODO: `/zeta/v1/contactredemption/count` often returns 500 errors
            // when using personal advertisable in prod; silence the error alerts for now.
            //
            // addAlert?.(FILTERED_CONTACTS_ERROR_MSG);
            setLoading(false);
        }
    }, [advertisableEid, crm, query]);
    useEffect(() => {
        if (!advertisableEid || !crm || !show || !accountRules || !contactRules) {
            return;
        }
        fetch();
    }, [advertisableEid, crm, show, accountRules, activityQuery, contactRules]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * label text of the preview button
     */
    const label = useMemo(() => {
        if (loading) {
            return i18n.gettext('Finding contacts');
        }
        if (total === undefined || Number.isNaN(Number(total?.count))) {
            return i18n.gettext('Preview contacts');
        }
        return i18n.gettext('{{ total }} contacts', { total: i18n.numeral(total?.count).format('0,0') });
    }, [loading, total]);

    return (
        <>
            <button
                type='button'
                role='button'
                disabled={!accountRules || loading || total === undefined}
                onClick={() => setOpen(true)}
                className='btn btn-default query-preview-btn preview-btn'
                style={{ display: show ? 'block' : 'none' }}
            >
                {!loading && <i className='fas fa-eye mr-1' />}
                {label}
                {loading && <div className='ar-spinner' />}
            </button>

            <AccountActionCrmContactFilterPreview
                {...props}
                query={query}
                total={total?.count}
                open={open}
                setOpen={setOpen}
                isContactEngagementOn={!!activityQuery}
            />
        </>
    );
};
